import { indexedDBManager } from "./system/indexed-db";
import { logger } from "../utils/logger";

/**
 * One-time newest-copy-wins migration of legacy localStorage DOCUMENTS into the
 * IndexedDB document store — Bombproof storage foundation, step 4 (board
 * 12-imgstore; approval-gate §6.1/§7).
 *
 * Under the ratified single-tier design (approval-gate §7: require IndexedDB;
 * IndexedDB is the SOLE document tier; localStorage = tiny pointers only), every
 * `project_*` record still sitting in localStorage is a LEGACY document copy from
 * the dual-write era. This module reconciles each one into IndexedDB and deletes
 * the localStorage copy — scoped to the migration window only (once localStorage
 * carries no documents, the scan finds nothing and the window is closed).
 *
 * FRESHEST-WINS (NA-B1″, R4-v2): the winner per key is the copy with the NEWER
 * record-intrinsic timestamp — `lastSaved` for `_state` manifests
 * (projectStateSaver.ts:85), `modified`/`created` for space/page documents
 * (spaceService.ts:130), `metadata.modified` for project metadata
 * (projectManager._doWriteProjectState). A fixed "IDB always wins" would
 * mirror-image the data loss (a degraded session's `.some()` dual-write can leave
 * localStorage newer). Where freshness is NOT determinable on both copies
 * (registries and other timestamp-less records), the localStorage copy wins the
 * tie — that is exactly what the shipped LS-first loader serves today, so the
 * tie-break is behavior-preserving by construction.
 *
 * DURABILITY BEFORE DELETION (NA-B2″ discipline): a localStorage copy is deleted
 * ONLY after the winning copy is confirmed present in IndexedDB by a read-back
 * compare (put alone is not durability evidence). Any failure — corrupt
 * localStorage value, IDB write failure, read-back mismatch — leaves the
 * localStorage copy IN PLACE (loud, counted in `leftInPlace`); the migration
 * simply re-runs at the next boot until the window closes. The migration never
 * throws.
 *
 * REGISTRY-INDEPENDENT (s3 sweep precedent): the scan enumerates raw localStorage
 * keys matching the `project_` document namespace — never a registry (registries
 * under-enumerate). Reserved `stagecraft_*` keys (project list, current-project
 * pointer, offline queue, legacy `stagecraft_project`) never match by
 * construction. `user_<ownerId>_project_*` keys are outside the namespace, same
 * boundary as `projectStorageSweep` (s3).
 *
 * PER-PROJECT `migrated` FLAG (approval-gate §7): after ALL of a project's
 * localStorage document copies are reconciled with zero failures, a
 * `project_<id>_migrated` marker record is written to IndexedDB. The flag lives
 * under the `project_<id>_` prefix, so a project delete/reset sweeps it with
 * everything else. The flag records that the one-time migration completed; the
 * scan itself stays authoritative (a re-appeared legacy copy is re-reconciled on
 * the next run regardless of the flag — freshest-wins keeps that lossless).
 *
 * ⛔ BOOT WIRING DEFERRED TO STEP 6 (NA-B2″): this module is authored + gated in
 * imgstore-fix-s4 but NOT yet invoked in production. The boot invocation lands
 * with the step-6 tier flip, ordered ensureLegacyDocumentsMigrated() FIRST, then
 * bootOrphanSweep() — the sweep must never run before an un-migrated LS-only
 * document has been copied to IndexedDB.
 */

export interface LegacyMigrationResult {
  /** Every `project_*` localStorage key the scan found. */
  scanned: string[];
  /** Keys whose localStorage copy won and was written + read-back-confirmed into IndexedDB. */
  copiedToIdb: string[];
  /** Keys where the IndexedDB copy was strictly newer (kept; localStorage copy was stale). */
  keptNewerIdb: string[];
  /** localStorage copies deleted after the winner was confirmed durable in IndexedDB. */
  deletedLsCopies: string[];
  /** localStorage keys deliberately LEFT (corrupt value or unconfirmed IndexedDB durability). */
  leftInPlace: string[];
  /** Project ids whose `project_<id>_migrated` flag was written this run. */
  flaggedProjects: string[];
}

/** True iff `key` is in the project document namespace (same boundary as the s3 sweep). */
export function isProjectDocumentNamespaceKey(key: string): boolean {
  return key.startsWith("project_");
}

/**
 * Extract a record-intrinsic timestamp (ms) from a stored document, or null when
 * the record carries none. Families (approval-gate §6.1): `_state` → `lastSaved`;
 * space/page → `modified` then `created`; metadata → `metadata.modified` then
 * `metadata.created`. A `{ data: … }` envelope is unwrapped defensively (same
 * guard as ElementStorage.manifestScenesAreEmpty).
 */
export function recordTimestampMs(value: unknown): number | null {
  let v = value;
  if (
    v &&
    typeof v === "object" &&
    "data" in (v as Record<string, unknown>) &&
    (v as Record<string, unknown>).data &&
    typeof (v as Record<string, unknown>).data === "object"
  ) {
    v = (v as Record<string, unknown>).data;
  }
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const meta =
    o.metadata && typeof o.metadata === "object"
      ? (o.metadata as Record<string, unknown>)
      : undefined;
  const candidates = [
    o.lastSaved,
    o.modified,
    o.created,
    meta?.modified,
    meta?.created,
  ];
  for (const c of candidates) {
    if (typeof c === "string") {
      const ms = Date.parse(c);
      if (Number.isFinite(ms)) return ms;
    }
  }
  return null;
}

/** Enumerate the raw `project_*` localStorage keys (sync, fail-safe → []). */
function scanLegacyDocumentKeys(): string[] {
  const matches: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key !== null && isProjectDocumentNamespaceKey(key)) matches.push(key);
    }
  } catch {
    // localStorage unavailable — nothing to migrate.
  }
  return matches;
}

/**
 * Project id owning `key`. Project ids are UUIDs (no underscores), so the id is
 * the segment between `project_` and the next `_` (or end-of-key for the exact
 * metadata key). Only used to group keys for the per-project flag — the per-key
 * reconcile itself is id-agnostic.
 */
export function projectIdOfKey(key: string): string {
  const rest = key.slice("project_".length);
  const sep = rest.indexOf("_");
  return sep === -1 ? rest : rest.slice(0, sep);
}

type KeyOutcome =
  | "copied-to-idb"
  | "kept-newer-idb"
  | "left-corrupt"
  | "left-undurable"
  | "gone";

/**
 * Reconcile ONE legacy localStorage document key into IndexedDB (freshest-wins),
 * deleting the localStorage copy only after the winner is confirmed durable.
 */
async function reconcileKey(key: string): Promise<KeyOutcome> {
  // Read + parse the localStorage copy. A present-but-corrupt value is left in
  // place LOUDLY — never delete bytes we cannot prove are represented in IDB.
  const raw = localStorage.getItem(key);
  if (raw === null) return "gone"; // vanished mid-run; nothing to do, nothing to count
  let lsData: unknown;
  try {
    lsData = JSON.parse(raw);
  } catch (parseError) {
    logger.error(
      "projectStorageMigration: corrupt localStorage document left in place",
      {
        error:
          parseError instanceof Error
            ? parseError
            : new Error(String(parseError)),
        data: { key },
      },
    );
    return "left-corrupt";
  }

  // Read the IndexedDB copy (any failure here = no usable IDB copy).
  let idbData: unknown;
  let idbHasCopy = false;
  try {
    const idbResult = await indexedDBManager.load<unknown>(key);
    if (idbResult.success && idbResult.data !== undefined) {
      idbData = idbResult.data;
      idbHasCopy = true;
    }
  } catch {
    // DB unavailable / not found — treat as no IDB copy; durability is proven
    // (or not) by the write + read-back below.
  }

  // Freshest-wins: IndexedDB wins ONLY when both copies carry timestamps and the
  // IDB copy is strictly newer. Ties and timestamp-less records go to the
  // localStorage copy — the copy the shipped LS-first loader serves today.
  const tsLs = recordTimestampMs(lsData);
  const tsIdb = idbHasCopy ? recordTimestampMs(idbData) : null;
  const idbWins =
    idbHasCopy && tsLs !== null && tsIdb !== null && tsIdb > tsLs;

  if (!idbWins) {
    // localStorage copy is the winner → write it to IndexedDB and CONFIRM by
    // read-back compare before deleting the localStorage copy.
    try {
      const saveResult = await indexedDBManager.save(key, lsData);
      if (!saveResult.success) throw new Error("IndexedDB save reported failure");
      const readBack = await indexedDBManager.load<unknown>(key);
      if (
        !readBack.success ||
        JSON.stringify(readBack.data) !== JSON.stringify(lsData)
      ) {
        throw new Error("IndexedDB read-back compare failed");
      }
    } catch (writeError) {
      logger.error(
        "projectStorageMigration: IndexedDB durability unconfirmed — localStorage copy left in place",
        {
          error:
            writeError instanceof Error
              ? writeError
              : new Error(String(writeError)),
          data: { key },
        },
      );
      return "left-undurable";
    }
  }

  // Winner is durably in IndexedDB (freshly confirmed, or the already-loaded
  // newer IDB copy). The localStorage copy — migrated or stale — is deleted.
  try {
    localStorage.removeItem(key);
  } catch {
    // Removal failure is harmless: the next run re-reconciles idempotently.
  }
  return idbWins ? "kept-newer-idb" : "copied-to-idb";
}

/**
 * Run the one-time migration over every legacy `project_*` localStorage document.
 * Idempotent, per-key fail-safe, never throws.
 */
export async function migrateLegacyProjectDocuments(): Promise<LegacyMigrationResult> {
  const result: LegacyMigrationResult = {
    scanned: [],
    copiedToIdb: [],
    keptNewerIdb: [],
    deletedLsCopies: [],
    leftInPlace: [],
    flaggedProjects: [],
  };

  try {
    result.scanned = scanLegacyDocumentKeys();
    if (result.scanned.length === 0) return result; // window closed — zero IDB work

    const failuresByProject = new Map<string, number>();
    for (const key of result.scanned) {
      const projectId = projectIdOfKey(key);
      if (!failuresByProject.has(projectId)) failuresByProject.set(projectId, 0);
      const outcome = await reconcileKey(key);
      switch (outcome) {
        case "copied-to-idb":
          result.copiedToIdb.push(key);
          result.deletedLsCopies.push(key);
          break;
        case "kept-newer-idb":
          result.keptNewerIdb.push(key);
          result.deletedLsCopies.push(key);
          break;
        case "left-corrupt":
        case "left-undurable":
          result.leftInPlace.push(key);
          failuresByProject.set(
            projectId,
            (failuresByProject.get(projectId) ?? 0) + 1,
          );
          break;
        case "gone":
          break;
      }
    }

    // Per-project migrated flag — only when EVERY key of that project reconciled.
    for (const [projectId, failures] of failuresByProject) {
      if (failures > 0) continue;
      try {
        const flagResult = await indexedDBManager.save(
          `project_${projectId}_migrated`,
          { migratedAt: new Date().toISOString() },
        );
        if (flagResult.success) result.flaggedProjects.push(projectId);
      } catch {
        // Flag is advisory; the scan stays authoritative. Next run retries.
      }
    }

    logger.info("projectStorageMigration: legacy document migration complete", {
      data: {
        scanned: result.scanned.length,
        copiedToIdb: result.copiedToIdb.length,
        keptNewerIdb: result.keptNewerIdb.length,
        deletedLsCopies: result.deletedLsCopies.length,
        leftInPlace: result.leftInPlace,
        flaggedProjects: result.flaggedProjects,
      },
    });
  } catch (error) {
    logger.error("projectStorageMigration: migration run failed (non-fatal)", {
      error: error instanceof Error ? error : new Error(String(error)),
    });
  }

  return result;
}

// ─── Session singleton (the step-6 boot wiring awaits this) ───────────────────

let migrationRun: Promise<LegacyMigrationResult> | null = null;

/**
 * Run the migration at most once per session; every caller awaits the same run.
 * Step 6 wires this at boot BEFORE bootOrphanSweep (NA-B2″ ordering).
 */
export function ensureLegacyDocumentsMigrated(): Promise<LegacyMigrationResult> {
  if (!migrationRun) migrationRun = migrateLegacyProjectDocuments();
  return migrationRun;
}

/** Test-only: drop the session singleton so each cell drives a fresh run. */
export function resetLegacyMigrationForTests(): void {
  migrationRun = null;
}
